'use client';

import { useState } from 'react';

import { useChangePasswordMutation } from '@/features/auth/api/authApi';
import {
  changePasswordSchema,
  changePasswordSchemaBase,
} from '@/features/auth/schemas';
import styles from '@/features/users/components/ProfilePanel.module.scss';
import { MainButton, SmartInput } from '@web/ui';
import {
  backendErrorsToFieldErrors,
  getErrorMessage,
  notify,
  zodErrorsToFieldErrors,
} from '@web/utils';

type ChangePasswordForm = {
  current_password: string;
  new_password: string;
  confirm_new_password: string;
};

type ChangePasswordField = keyof ChangePasswordForm;
type ChangePasswordErrors = Partial<Record<ChangePasswordField, string>>;

function mapBackendErrorsToFormErrors(
  backendErrors: unknown,
): ChangePasswordErrors {
  const flatErrors = backendErrorsToFieldErrors(backendErrors);
  const formErrors: ChangePasswordErrors = {};
  const acceptedKeys: ChangePasswordField[] = [
    'current_password',
    'new_password',
    'confirm_new_password',
  ];

  acceptedKeys.forEach((key) => {
    if (flatErrors[key]) {
      formErrors[key] = flatErrors[key];
    }
  });

  return formErrors;
}

function extractApiErrorDetails(error: unknown): {
  message?: string;
  errors?: unknown;
} {
  if (
    typeof error === 'object' &&
    error !== null &&
    'data' in error &&
    typeof (error as { data?: unknown }).data === 'object' &&
    (error as { data?: unknown }).data !== null
  ) {
    const data = (error as { data: Record<string, unknown> }).data;
    return {
      message: typeof data.message === 'string' ? data.message : undefined,
      errors: data.errors,
    };
  }

  return {};
}

const EMPTY_FORM: ChangePasswordForm = {
  current_password: '',
  new_password: '',
  confirm_new_password: '',
};

export function ChangePasswordSection() {
  const [form, setForm] = useState<ChangePasswordForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<ChangePasswordErrors>({});
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrors({});

    const parsedForm = changePasswordSchema.safeParse(form);
    if (!parsedForm.success) {
      setErrors(zodErrorsToFieldErrors<ChangePasswordForm>(parsedForm.error));
      return;
    }

    try {
      const response = await changePassword({
        current_password: parsedForm.data.current_password,
        new_password: parsedForm.data.new_password,
      }).unwrap();

      if (!response.success) {
        const fieldErrors = mapBackendErrorsToFormErrors(response.errors);
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((previous) => ({ ...previous, ...fieldErrors }));
        }
        notify({
          type: 'error',
          message: response.message || 'Unable to update password.',
        });
        return;
      }

      setForm(EMPTY_FORM);
      notify({
        type: 'success',
        message: response.message || 'Password updated successfully.',
      });
    } catch (error) {
      const errorDetails = extractApiErrorDetails(error);
      const fieldErrors = mapBackendErrorsToFormErrors(errorDetails.errors);
      if (Object.keys(fieldErrors).length > 0) {
        setErrors((previous) => ({ ...previous, ...fieldErrors }));
      }
      notify({
        type: 'error',
        message: errorDetails.message || getErrorMessage(error),
      });
    }
  }

  return (
    <section className={`card-container-glass ${styles.card}`}>
      <header className={styles.cardHeader}>
        <h2 className={styles.cardTitle}>Change password</h2>
        <p className={styles.cardDescription}>Update your password.</p>
      </header>

      <form className={styles.form} onSubmit={handleSubmit}>
        <SmartInput
          type="password"
          name="current_password"
          label="Current password"
          value={form.current_password}
          onChange={(value) => {
            setForm((previous) => ({
              ...previous,
              current_password: value,
            }));
            if (errors.current_password) {
              setErrors((previous) => ({
                ...previous,
                current_password: undefined,
              }));
            }
          }}
          schema={changePasswordSchemaBase.shape.current_password}
          error={errors.current_password}
        />

        <SmartInput
          type="password"
          name="new_password"
          label="New password"
          value={form.new_password}
          onChange={(value) => {
            setForm((previous) => ({
              ...previous,
              new_password: value,
            }));
            if (errors.new_password) {
              setErrors((previous) => ({
                ...previous,
                new_password: undefined,
              }));
            }
          }}
          schema={changePasswordSchemaBase.shape.new_password}
          error={errors.new_password}
        />

        <SmartInput
          type="password"
          name="confirm_new_password"
          label="Confirm new password"
          value={form.confirm_new_password}
          onChange={(value) => {
            setForm((previous) => ({
              ...previous,
              confirm_new_password: value,
            }));
            if (errors.confirm_new_password) {
              setErrors((previous) => ({
                ...previous,
                confirm_new_password: undefined,
              }));
            }
          }}
          schema={changePasswordSchemaBase.shape.confirm_new_password}
          error={errors.confirm_new_password}
        />

        <div className={styles.actions}>
          <MainButton type="submit" isLoading={isLoading} disabled={isLoading}>
            Update password
          </MainButton>
        </div>
      </form>
    </section>
  );
}
